// Reporting "Type" filter — curated campaign-type options (shared client+server).
//
// The dashboard's Type filter no longer passes raw Meta objective codes. Instead
// it offers a curated list built from the distinct objectives present in the
// data, with OUTCOME_LEADS expanded into three lead sub-options (On-Meta Lead /
// Website Lead / All Lead) so the split matches the cost-per-meta-lead classifier
// exactly. This module is PURE + Vite-safe (no server imports) — the server-side
// SQL translation lives next to META_FORM_LEAD_SQL
// (server/modules/reporting/query/campaignTypePredicate.ts).

// Friendly labels for the known Meta ODAX objectives. Unknown objectives fall
// through to their raw code so a new/legacy objective stays discoverable.
const OBJECTIVE_LABELS: Record<string, string> = {
  OUTCOME_LEADS: 'Leads',
  OUTCOME_TRAFFIC: 'Traffic',
  OUTCOME_ENGAGEMENT: 'Engagement',
  OUTCOME_SALES: 'Sales',
  OUTCOME_AWARENESS: 'Awareness',
  OUTCOME_APP_PROMOTION: 'App',
};

export function objectiveLabel(code: string): string {
  return OBJECTIVE_LABELS[code] ?? code;
}

export interface CampaignTypeOption {
  key: string;
  label: string;
}

// The three lead sub-options, emitted (in this order) when OUTCOME_LEADS is
// present. Their keys are the contract with typePredicateSql (server-side).
export const LEAD_TYPE_OPTIONS: CampaignTypeOption[] = [
  { key: 'on_meta_lead', label: 'On-Meta Lead' },
  { key: 'website_lead', label: 'Website Lead' },
  { key: 'all_lead', label: 'All Lead' },
];

// Build the curated Type options from the distinct objectives present in the
// data: the 3 lead sub-options first (only when OUTCOME_LEADS exists), then one
// `obj:<code>` option per other distinct objective, alphabetical by label.
export function buildTypeOptions(distinctObjectives: string[]): CampaignTypeOption[] {
  const distinct = Array.from(new Set(distinctObjectives));
  const options: CampaignTypeOption[] = [];
  if (distinct.includes('OUTCOME_LEADS')) options.push(...LEAD_TYPE_OPTIONS);
  const others = distinct
    .filter((o) => o !== 'OUTCOME_LEADS')
    .sort((a, b) => objectiveLabel(a).localeCompare(objectiveLabel(b)));
  for (const code of others) options.push({ key: `obj:${code}`, label: objectiveLabel(code) });
  return options;
}

// ---------------------------------------------------------------------------
// Per-campaign Type CLASSIFICATION (reporting explorer attribute chip).
// ---------------------------------------------------------------------------
// A campaign's short type label + a colour CATEGORY (for the tinted chip's
// categorical ramp — NEVER the semantic green/red reserved for status). Mirrors
// the OUTCOME_LEADS instant-form-vs-website split of META_FORM_LEAD_SQL: a lead
// campaign NOT optimising for offsite/pixel conversions is an on-Meta instant
// form; the offsite/pixel path is what makes it a website lead.

export type ReportingTypeCategory = 'lead' | 'traffic' | 'engagement' | 'awareness' | 'other';

export interface ReportingTypeClass {
  label: string; // short 1-2 word chip label
  category: ReportingTypeCategory; // drives the chip colour ramp
}

// The on-Meta instant-form test (client mirror of server META_FORM_LEAD_SQL):
// OUTCOME_LEADS whose optimization goal is NOT offsite/pixel conversions.
function isInstantForm(objective: string, optimizationGoal: string | null): boolean {
  return objective === 'OUTCOME_LEADS' && (optimizationGoal ?? '') !== 'OFFSITE_CONVERSIONS';
}

export function classifyCampaignType(
  objective: string | null,
  optimizationGoal: string | null,
): ReportingTypeClass {
  if (!objective) return { label: '—', category: 'other' };
  if (objective === 'OUTCOME_LEADS') {
    return isInstantForm(objective, optimizationGoal)
      ? { label: 'Instant form', category: 'lead' }
      : { label: 'Website', category: 'lead' };
  }
  switch (objective) {
    case 'OUTCOME_TRAFFIC':
    case 'LINK_CLICKS':
      return { label: 'Traffic', category: 'traffic' };
    case 'OUTCOME_ENGAGEMENT':
    case 'POST_ENGAGEMENT':
      return { label: 'Engagement', category: 'engagement' };
    case 'OUTCOME_AWARENESS':
      return { label: 'Awareness', category: 'awareness' };
    case 'OUTCOME_SALES':
      return { label: 'Sales', category: 'other' };
    default:
      return { label: objectiveLabel(objective), category: 'other' };
  }
}
